import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  initiatePaymentForm,
  selectAccounts,
} from '@cashier/cashier-data-access';
import { FormField } from '@cashier/cashier-model';

import { useAmountValueContext } from '../../../contexts';
import { useFieldToucher, useFormPayloadItem } from '../../../hooks';

interface UseAccountSelect {
  isDisabled: boolean;
  onChange: (value: string) => void;
  onReset: () => void;
}

export const useAccountSelect = (field: FormField): UseAccountSelect => {
  const dispatch = useDispatch();
  const { touch } = useFieldToucher();
  const accounts = useSelector(selectAccounts);

  const { value: initialAmount } = useAmountValueContext();

  const { name, value: initialValue } = field;

  const { isDisabled } = useFormPayloadItem(name);

  const onChange = useCallback(
    (value: string) => {
      if (initialValue) {
        touch({ ...field, value }, { interactionCompleted: true });
      } else {
        const accountConfiguration = accounts.find(
          (account) => account.value === value,
        );

        if (accountConfiguration) {
          dispatch(
            initiatePaymentForm({
              account: accountConfiguration,
              amount: initialAmount,
            }),
          );
        }
      }
    },
    [accounts, dispatch, initialAmount, initialValue, touch, field],
  );

  const onReset = useCallback(() => {
    if (!isDisabled && initialValue !== null) {
      dispatch(
        initiatePaymentForm({
          account: null,
          amount: initialAmount,
        }),
      );
    }
  }, [isDisabled, initialValue, dispatch, initialAmount]);

  return {
    isDisabled,
    onChange,
    onReset,
  };
};
